import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { Progress } from "@/components/ui/progress";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { MapCard } from "@/components/map/MapCard";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { ChevronLeft, MapPin, Check, ShieldCheck } from "lucide-react";

const SPORTS = ["tennis", "basketball", "soccer", "badminton", "cricket", "volleyball", "other"];

export default function BookingWizard() {
  const nav = useNavigate();
  const { user } = useAuth();
  const isVerified = user?.athlete_verification_status === "approved";
  const [step, setStep] = useState(0);
  const [sport, setSport] = useState("tennis");
  const [otherSport, setOtherSport] = useState("");
  const [pin, setPin] = useState({ lat: 40.758, lng: -73.9855 });
  const [address, setAddress] = useState("Times Square, New York");
  const [addressEdited, setAddressEdited] = useState(false);
  const [geocoding, setGeocoding] = useState(false);
  const [privateLoc, setPrivateLoc] = useState(false);
  const [favorites, setFavorites] = useState([]);
  const [selectedFav, setSelectedFav] = useState(null);
  const [saveAsFavOpen, setSaveAsFavOpen] = useState(false);
  const [favName, setFavName] = useState("");
  const [startAt, setStartAt] = useState(() => {
    const d = new Date();
    d.setHours(d.getHours() + 1, 0, 0, 0);
    return d.toISOString().slice(0, 16);
  });
  const [duration, setDuration] = useState([1]);
  const [rate, setRate] = useState([25]);
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const steps = ["Sport", "Location", "When", "Rate", "Confirm"];
  const progress = ((step + 1) / steps.length) * 100;

  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (pos) => setPin({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
        () => {}
      );
    }
    api.get("/favorite-locations").then((r) => setFavorites(r.data.items || [])).catch(() => {});
  }, []);

  // Auto-reverse-geocode pin whenever it changes (unless user has edited address manually).
  useEffect(() => {
    if (addressEdited) return;
    if (!pin || pin.lat == null || pin.lng == null) return;
    const ctrl = new AbortController();
    const t = setTimeout(async () => {
      try {
        setGeocoding(true);
        const { data } = await api.get(`/geo/reverse`, {
          params: { lat: pin.lat, lng: pin.lng },
          signal: ctrl.signal,
        });
        if (data?.ok && data.address) {
          setAddress(data.address);
        }
      } catch (_) {
        // graceful fallback — keep current address
      } finally {
        setGeocoding(false);
      }
    }, 400); // debounce map drags / double-taps
    return () => { clearTimeout(t); ctrl.abort(); };
  }, [pin.lat, pin.lng, addressEdited]);

  const total = useMemo(() => {
    const sub = duration[0] * rate[0];
    const fee = +(sub * 0.1).toFixed(2);
    return { sub, fee, total: +(sub + fee).toFixed(2) };
  }, [duration, rate]);

  async function submit() {
    const finalSport = sport === "other" ? otherSport.trim().toLowerCase() : sport;
    if (sport === "other" && !finalSport) {
      toast.error("Please specify the sport");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/bookings", {
        sport: finalSport,
        lat: pin.lat,
        lng: pin.lng,
        address,
        start_at: new Date(startAt).toISOString(),
        duration_hours: duration[0],
        hourly_rate: rate[0],
        notes,
        private_location: privateLoc,
      });
      toast.success(`Booking created. Notified ${data.notified_ballboys} nearby ball boys.`);
      nav(`/bookings/${data.booking.id}`);
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (d && typeof d === "object" && d.error === "verified_athlete_required") {
        toast.error(d.message, {
          description: `Flags: ${d.flags?.join(", ")}`,
          action: { label: "Verify now", onClick: () => nav("/athlete/verify") },
          duration: 15000,
        });
      } else {
        toast.error(typeof d === "string" ? d : "Failed to create booking");
      }
    } finally {
      setSubmitting(false);
    }
  }

  async function saveFavorite() {
    if (!favName.trim()) return toast.error("Name your favorite");
    try {
      const { data } = await api.post("/favorite-locations", {
        name: favName.trim(),
        lat: pin.lat, lng: pin.lng, address,
      });
      setFavorites((arr) => [data.favorite, ...arr]);
      setSaveAsFavOpen(false);
      setFavName("");
      toast.success("Favorite saved");
    } catch (e) { toast.error("Failed"); }
  }

  async function deleteFavorite(id) {
    try {
      await api.delete(`/favorite-locations/${id}`);
      setFavorites((arr) => arr.filter((f) => f.id !== id));
    } catch {}
  }

  return (
    <div className="space-y-4">
      {!isVerified && (
        <Card className="border-[hsl(var(--warning)/0.5)] bg-[hsl(var(--warning)/0.08)]" data-testid="booking-verification-required">
          <CardContent className="p-4 space-y-3">
            <div className="flex items-start gap-3">
              <div className="grid place-items-center h-10 w-10 rounded-full bg-[hsl(var(--warning)/0.18)] text-[hsl(var(--warning))]">
                <ShieldCheck className="h-5 w-5" />
              </div>
              <div className="flex-1">
                <div className="font-semibold bb-display">Identity verification required</div>
                <p className="text-sm text-muted-foreground mt-1">
                  Booking a ball boy is gated by an instant facial-recognition check (ID + selfie). Verify once and book anytime.
                </p>
              </div>
            </div>
            <Button className="w-full" onClick={() => nav("/athlete/verify")} data-testid="booking-verify-now-button">
              Verify identity now
            </Button>
          </CardContent>
        </Card>
      )}

      <div className="flex items-center gap-2">
        {step > 0 ? (
          <Button variant="ghost" size="icon" onClick={() => setStep((s) => s - 1)} aria-label="Back" data-testid="wizard-back-button">
            <ChevronLeft className="h-5 w-5" />
          </Button>
        ) : (
          <Button variant="ghost" size="icon" onClick={() => nav(-1)} aria-label="Back">
            <ChevronLeft className="h-5 w-5" />
          </Button>
        )}
        <div className="flex-1">
          <div className="text-xs text-muted-foreground">Step {step + 1} of {steps.length}</div>
          <div className="text-lg font-semibold bb-display">{steps[step]}</div>
        </div>
      </div>
      <Progress value={progress} />

      {step === 0 && (
        <Card>
          <CardHeader><CardTitle className="bb-display">Pick a sport</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            <Select value={sport} onValueChange={setSport}>
              <SelectTrigger data-testid="booking-sport-select" className="h-11">
                <SelectValue placeholder="Select sport" />
              </SelectTrigger>
              <SelectContent>
                {SPORTS.map((s) => (
                  <SelectItem key={s} value={s} data-testid={`sport-option-${s}`}>
                    <span className="capitalize">{s}</span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {sport === "other" && (
              <div className="space-y-1.5">
                <Label>Which sport?</Label>
                <Input
                  value={otherSport}
                  onChange={(e) => setOtherSport(e.target.value)}
                  placeholder="e.g. pickleball, squash, hockey"
                  data-testid="booking-other-sport-input"
                />
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {step === 1 && (
        <Card>
          <CardHeader><CardTitle className="bb-display">Where is the court?</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            {favorites.length > 0 && (
              <div>
                <Label>Favorites</Label>
                <div className="flex gap-2 overflow-x-auto py-1" data-testid="favorites-chips">
                  {favorites.map((f) => (
                    <div key={f.id} className="shrink-0 group relative">
                      <button
                        type="button"
                        onClick={() => { setPin({ lat: f.lat, lng: f.lng }); setAddress(f.address || f.name); setAddressEdited(true); setSelectedFav(f.id); }}
                        className={`px-3 py-1.5 text-xs rounded-full border capitalize ${selectedFav === f.id ? "bg-primary text-primary-foreground border-primary" : "bg-card border-border"}`}
                        data-testid={`favorite-chip-${f.id}`}
                      >
                        ⭐ {f.name}
                      </button>
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); deleteFavorite(f.id); }}
                        className="absolute -top-1 -right-1 hidden group-hover:grid place-items-center h-4 w-4 rounded-full bg-destructive text-destructive-foreground text-[10px]"
                        aria-label="Delete favorite"
                      >×</button>
                    </div>
                  ))}
                </div>
              </div>
            )}
            <MapCard
              center={[pin.lat, pin.lng]}
              pickable
              onPick={(p) => { setPin(p); setSelectedFav(null); setAddressEdited(false); }}
              pins={[{ lat: pin.lat, lng: pin.lng, variant: "athlete", label: "Pickup" }]}
              testId="booking-location-map"
              height={260}
            />
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <MapPin className="h-4 w-4" />
              Tap the map to set the pickup location.
            </div>
            <div className="space-y-1.5">
              <Label>
                Address / landmark
                {geocoding && <span className="text-xs text-muted-foreground ml-2" data-testid="booking-address-geocoding">Looking up address…</span>}
              </Label>
              <Input
                value={address}
                onChange={(e) => { setAddress(e.target.value); setAddressEdited(true); }}
                data-testid="booking-address-input"
              />
            </div>
            <div className="flex gap-2">
              {saveAsFavOpen ? (
                <div className="flex gap-2 flex-1">
                  <Input value={favName} onChange={(e) => setFavName(e.target.value)} placeholder="Name e.g. 'Home court'" data-testid="save-favorite-name-input" />
                  <Button onClick={saveFavorite} data-testid="save-favorite-submit">Save</Button>
                  <Button variant="ghost" onClick={() => setSaveAsFavOpen(false)}>Cancel</Button>
                </div>
              ) : (
                <Button variant="secondary" onClick={() => setSaveAsFavOpen(true)} data-testid="save-favorite-open">⭐ Save as favorite</Button>
              )}
            </div>
            <label className="flex items-center gap-2 text-sm mt-1">
              <input type="checkbox" checked={privateLoc} onChange={(e) => setPrivateLoc(e.target.checked)} data-testid="booking-private-location" />
              This is a <strong>private / non-public</strong> location
            </label>
            {privateLoc && (
              <p className="text-xs text-[hsl(var(--warning))]">Private-location bookings require a Verified Athlete badge.</p>
            )}
          </CardContent>
        </Card>
      )}

      {step === 2 && (
        <Card>
          <CardHeader><CardTitle className="bb-display">When?</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            <div className="space-y-1.5">
              <Label>Start time</Label>
              <Input type="datetime-local" value={startAt} onChange={(e) => setStartAt(e.target.value)} data-testid="booking-start-at-input" />
            </div>
            <div className="space-y-1.5">
              <Label>Duration: <span className="font-semibold tabular-nums">{duration[0]}</span> hour{duration[0] > 1 ? "s" : ""}</Label>
              <Slider min={0.5} max={4} step={0.5} value={duration} onValueChange={setDuration} data-testid="booking-duration-slider" />
            </div>
          </CardContent>
        </Card>
      )}

      {step === 3 && (
        <Card>
          <CardHeader><CardTitle className="bb-display">Rate & notes</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            <div className="space-y-1.5">
              <Label>Hourly rate: <span className="font-semibold tabular-nums">${rate[0]}</span>/hr</Label>
              <Slider min={10} max={100} step={1} value={rate} onValueChange={setRate} data-testid="booking-rate-slider" />
            </div>
            <div className="space-y-1.5">
              <Label>Notes (optional)</Label>
              <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Court gate code, dress code, etc." data-testid="booking-notes-input" />
            </div>
          </CardContent>
        </Card>
      )}

      {step === 4 && (
        <Card>
          <CardHeader><CardTitle className="bb-display">Confirm</CardTitle></CardHeader>
          <CardContent className="space-y-3 text-sm">
            <Row label="Sport"><span className="capitalize">{sport === "other" ? (otherSport || "Other") : sport}</span></Row>
            <Row label="Location">{address}</Row>
            <Row label="Start">{new Date(startAt).toLocaleString()}</Row>
            <Row label="Duration">{duration[0]} hour{duration[0] > 1 ? "s" : ""}</Row>
            <Row label="Hourly rate"><span className="tabular-nums">${rate[0]}</span></Row>
            <div className="pt-3 border-t border-border">
              <Row label="Subtotal"><span className="tabular-nums">${total.sub.toFixed(2)}</span></Row>
              <Row label="Platform fee (10%)"><span className="tabular-nums">${total.fee.toFixed(2)}</span></Row>
              <Row label="Total"><span className="font-semibold tabular-nums">${total.total.toFixed(2)}</span></Row>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="pt-2">
        {step < steps.length - 1 ? (
          <Button
            className="w-full h-12"
            onClick={() => setStep((s) => s + 1)}
            disabled={step === 0 && sport === "other" && !otherSport.trim()}
            data-testid="wizard-next-button"
          >
            Continue
          </Button>
        ) : (
          <Button className="w-full h-12" onClick={submit} disabled={submitting || !isVerified} data-testid="booking-submit-button">
            {submitting ? "Requesting…" : !isVerified ? (<><ShieldCheck className="h-4 w-4 mr-2" /> Verify identity to request</>) : (<><Check className="h-4 w-4 mr-2" /> Request ball boy</>)}
          </Button>
        )}
      </div>
    </div>
  );
}

function Row({ label, children }) {
  return (
    <div className="flex justify-between py-1">
      <div className="text-muted-foreground">{label}</div>
      <div>{children}</div>
    </div>
  );
}
